/* eslint-disable */
import {
  ActivityIndicator,
  Animated,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, {Defs, LinearGradient, Path, Rect, Stop} from 'react-native-svg';
import {useEffect, useRef, useState} from 'react';
import {useIsFocused} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import AuthHeader from '../../components/AuthHeader';
import {getRewards} from '../../services/rewards-service';
import {useScreenWidth} from '../../utility/useDesktopLayout';
import {colors, card, fonts, radii, spacing, typography} from '../../config/theme';
import giftHero from '../../assets/rewards/gift-hero.jpg';

const HERO_MAX = 240;
const GRID_GAP = 10;
const GRID_COLUMNS = 3;
const BAR_HEIGHT = 8;
const TILE_LOGO_RING = 56;
const STATUS_BADGE = 20;

// Progress bar ends from Stitch "Pompi - Rewards".
const barTrack = 'rgba(2, 44, 34, 0.6)';
const scanHighlight = '#1CD17A';

const Rewards = () => {
  const {t} = useTranslation();
  const isFocused = useIsFocused();
  const width = useScreenWidth();
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(false);
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRewards()
      .then(data => !cancelled && setRewards(data))
      .catch(err => console.log('getRewards error', err?.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isFocused]);

  const targets = rewards?.targets || [];
  const activeTarget = rewards?.activeTarget;
  const lastTarget = targets[targets.length - 1];
  // With every tier done there is no active target: show the last one as full.
  const target = activeTarget?.scansRequired ?? lastTarget?.scansRequired ?? 0;
  const scanCount = activeTarget
    ? activeTarget.scansDone
    : rewards?.allTiersCompleted
    ? target
    : rewards?.scans.approved || 0;
  const remaining = activeTarget
    ? activeTarget.scansRemaining
    : Math.max(0, target - scanCount);
  const currentReward = activeTarget?.coupon;
  const fill = target > 0 ? Math.min(1, scanCount / target) : 0;

  // 'unlocked' | 'active' | 'locked'. Anything the backend marks other than
  // active/locked (or with a reward attached) counts as unlocked.
  const tileState = item => {
    if (
      rewards?.allTiersCompleted ||
      item.reward ||
      (item.state !== 'active' && item.state !== 'locked')
    ) {
      return 'unlocked';
    }
    return item.state === 'active' || item.level === activeTarget?.level
      ? 'active'
      : 'locked';
  };
  const targetName = currentReward?.offerText || currentReward?.brandName;

  useEffect(() => {
    progress.setValue(0);
    Animated.timing(progress, {
      toValue: fill,
      duration: 500,
      useNativeDriver: false,
    }).start();
  }, [fill, progress, isFocused]);

  // Spinner only on the very first load; later refreshes keep the content.
  if (!rewards && loading) {
    return (
      <View style={styles.screen}>
        <AuthHeader title={t('root.Rewards.title')} />
        <ActivityIndicator
          size="large"
          color={colors.primary}
          style={styles.loading}
        />
      </View>
    );
  }

  // Floored so rounding never pushes the third tile onto a new row.
  const tileWidth = Math.floor(
    (width - spacing.margin * 2 - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS,
  );
  // Gift emblem from Stitch "Pompi - Rewards", flattened onto white so it
  // blends into the card.
  const heroSize = Math.min(HERO_MAX, Math.round(width * 0.62));
  const barWidth = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <View style={styles.screen}>
      <AuthHeader title={t('root.Rewards.title')} />
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <View style={[styles.card, styles.hero]}>
          <Image
            source={giftHero}
            style={{width: heroSize, height: heroSize}}
            resizeMode="contain"
            accessibilityIgnoresInvertColors
          />
          <Text style={styles.heroHint}>{t('root.Rewards.unlockHint')}</Text>
        </View>

        <View style={styles.tracker}>
          <TrackerBackground />
          <View style={styles.trackerRow}>
            <Text style={styles.trackerCount}>
              {scanCount}
              <Text style={styles.trackerTarget}> /{target}</Text>
            </Text>
            <View style={styles.trackerText}>
              {activeTarget ? (
                <Text style={styles.trackerEyebrow} numberOfLines={1}>
                  {t('root.Rewards.tierInProgress', {
                    level: activeTarget.level,
                  })}
                </Text>
              ) : null}
              <Text style={styles.trackerMessage}>
                {remaining > 0 ? (
                  <>
                    {t('root.Rewards.Great')},{' '}
                    <Text style={styles.trackerHighlight}>
                      {t('root.Rewards.moreScans', {count: remaining})}
                    </Text>{' '}
                    {targetName
                      ? t('root.Rewards.toAchieve', {reward: targetName})
                      : t('root.Rewards.toTarget')}
                  </>
                ) : (
                  <Text style={styles.trackerHighlight}>
                    {t('root.Rewards.targetReached')}
                  </Text>
                )}
              </Text>
            </View>
          </View>
          <View style={styles.barTrack}>
            <Animated.View style={[styles.barFill, {width: barWidth}]} />
          </View>
          {targets.length > 0 ? (
            <View style={styles.milestones}>
              {targets.map(item => {
                const state = tileState(item);
                return (
                  <Text
                    key={`milestone-${item.level}`}
                    style={[
                      styles.milestone,
                      state === 'unlocked' && styles.milestoneUnlocked,
                      state === 'active' && styles.milestoneActive,
                    ]}
                    numberOfLines={1}>
                    {state === 'unlocked' ? '✓ ' : ''}
                    {item.scansRequired} {item.coupon?.brandName || item.tierName}
                  </Text>
                );
              })}
            </View>
          ) : null}
        </View>

        {targets.length > 0 ? (
          <View style={styles.targets}>
            <Text style={styles.targetsTitle} accessibilityRole="header">
              {t('root.Rewards.Targets')}
            </Text>
            <View style={styles.grid}>
              {targets.map((item, index) => {
                const state = tileState(item);
                return (
                  <TargetTile
                    key={`reward-${item.level}-${index}`}
                    item={item}
                    width={tileWidth}
                    state={state}
                    badgeLabel={
                      state === 'unlocked'
                        ? t('root.Rewards.unlocked')
                        : state === 'active'
                        ? t('root.Rewards.activeTarget')
                        : null
                    }
                    tagLabel={
                      state === 'unlocked'
                        ? t('root.Rewards.claimed')
                        : state === 'active'
                        ? t('root.Rewards.scansProgress', {
                            done: scanCount,
                            total: item.scansRequired,
                          })
                        : `${item.scansRequired} ${t('root.Rewards.Scans')}`
                    }
                    lockedLabel={t('root.Rewards.locked')}
                  />
                );
              })}
            </View>
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
};

// Gift-box icon used as the placeholder when a partner has no logo.
const GiftIcon = ({size, color}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8M2 8h20v4H2zM12 8v13"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M12 8H7.5a2.5 2.5 0 1 1 0-5C11 3 12 8 12 8zM12 8h4.5a2.5 2.5 0 1 0 0-5C13 3 12 8 12 8z"
      fill="none"
      stroke={color}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Partner logo from the API. A placeholder (gift icon on a muted tile) shows
// when there is no logo, while it loads, and if it fails to load, so the tile
// is never left blank.
const BrandLogo = ({uri, size, radius}) => {
  const [failed, setFailed] = useState(false);

  // a new logo link gets a fresh try
  useEffect(() => {
    setFailed(false);
  }, [uri]);

  if (!uri || failed) {
    return (
      <View
        style={[
          styles.logoPlaceholder,
          {width: size, height: size, borderRadius: radius},
        ]}>
        <GiftIcon size={Math.round(size * 0.55)} color={colors.textSubtle} />
      </View>
    );
  }
  return (
    <Image
      source={{uri}}
      // the muted background is what shows while the logo is still loading
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        backgroundColor: colors.surfaceMuted,
      }}
      resizeMode="cover"
      onError={() => setFailed(true)}
    />
  );
};

const CheckIcon = ({size, color}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M5 12.5l4.5 4.5L19 7.5"
      fill="none"
      stroke={color}
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const LockIcon = ({size, color}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M8 11V8a4 4 0 0 1 8 0v3"
      fill="none"
      stroke={color}
      strokeWidth={2.5}
      strokeLinecap="round"
    />
    <Rect x={5} y={11} width={14} height={10} rx={2.5} fill={color} />
  </Svg>
);

// One reward tier: unlocked (mint, check, claimed), active (outlined in
// brand green, live scan count) or locked (faded, padlock).
const TargetTile = ({item, width, state, badgeLabel, tagLabel, lockedLabel}) => {
  const unlocked = state === 'unlocked';
  const active = state === 'active';
  const locked = state === 'locked';
  const title = item.coupon?.offerText || item.coupon?.brandName || item.tierName;
  return (
    <View
      style={[
        styles.card,
        styles.tile,
        {width},
        unlocked && styles.tileUnlocked,
        active && styles.tileActive,
      ]}
      accessible
      accessibilityLabel={[badgeLabel || lockedLabel, title, tagLabel]
        .filter(Boolean)
        .join(', ')}>
      {badgeLabel ? (
        <View
          style={[
            styles.tileBadge,
            unlocked ? styles.tileBadgeUnlocked : styles.tileBadgeActive,
          ]}>
          <Text
            style={[
              styles.tileBadgeText,
              active && styles.tileBadgeTextActive,
            ]}
            numberOfLines={1}>
            {badgeLabel}
          </Text>
        </View>
      ) : null}
      <View>
        <View
          style={[
            styles.tileRing,
            unlocked && styles.tileRingUnlocked,
            locked && styles.faded,
          ]}>
          <BrandLogo uri={item.coupon?.logoUrl} size={40} radius={8} />
        </View>
        {unlocked ? (
          <View style={[styles.statusBadge, styles.statusBadgeCheck]}>
            <CheckIcon size={11} color={colors.onPrimary} />
          </View>
        ) : null}
        {locked ? (
          <View style={[styles.statusBadge, styles.statusBadgeLock]}>
            <LockIcon size={10} color={colors.onPrimary} />
          </View>
        ) : null}
      </View>
      <Text
        style={[styles.tileLabel, locked && styles.tileLabelLocked]}
        numberOfLines={2}>
        {title}
      </Text>
      <View
        style={[
          styles.tileTag,
          unlocked && styles.tileTagUnlocked,
          active && styles.tileTagActive,
        ]}>
        <Text
          style={[
            styles.tileTagText,
            (unlocked || active) && styles.tileTagTextOnColor,
          ]}
          numberOfLines={1}>
          {tagLabel}
        </Text>
      </View>
    </View>
  );
};

// Forest gradient behind the scan tracker.
const TrackerBackground = () => (
  <Svg style={StyleSheet.absoluteFill} pointerEvents="none">
    <Defs>
      <LinearGradient id="trackerFill" x1="0" y1="0" x2="1" y2="0">
        <Stop offset="0" stopColor={colors.primary} />
        <Stop offset="1" stopColor={colors.brandMid} />
      </LinearGradient>
    </Defs>
    <Rect width="100%" height="100%" fill="url(#trackerFill)" />
  </Svg>
);

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  loading: {
    marginTop: spacing.xxl,
  },
  content: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.margin,
    paddingBottom: spacing.xxl,
    gap: spacing.margin,
  },
  card: {
    ...card,
  },

  // Featured reward
  hero: {
    borderRadius: 24,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
    alignItems: 'center',
  },
  heroHint: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  logoPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceMuted,
  },

  // Scan tracker
  tracker: {
    borderRadius: radii.card,
    overflow: 'hidden',
    padding: spacing.margin,
  },
  trackerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.margin,
  },
  trackerCount: {
    fontFamily: fonts.bold,
    fontSize: 30,
    lineHeight: 36,
    letterSpacing: -0.6,
    color: colors.onPrimary,
  },
  trackerTarget: {
    fontFamily: fonts.semiBold,
    fontSize: 20,
    letterSpacing: 0,
    color: colors.onPrimaryMuted,
  },
  trackerText: {
    flex: 1,
    gap: 2,
  },
  trackerEyebrow: {
    ...typography.labelSm,
    fontFamily: fonts.bold,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: colors.tertiary,
  },
  trackerMessage: {
    fontFamily: fonts.medium,
    fontSize: 13,
    lineHeight: 18,
    color: colors.onPrimary,
  },
  trackerHighlight: {
    fontFamily: fonts.bold,
    color: scanHighlight,
  },
  barTrack: {
    height: BAR_HEIGHT,
    marginTop: spacing.md,
    borderRadius: radii.pill,
    backgroundColor: barTrack,
    overflow: 'hidden',
  },
  // Solid fill: an SVG gradient inside a width-animated view doesn't
  // redraw as the view grows, so the bar stayed invisible.
  barFill: {
    height: '100%',
    borderRadius: radii.pill,
    backgroundColor: scanHighlight,
  },

  milestones: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    columnGap: spacing.md,
    rowGap: spacing.xs,
    marginTop: spacing.sm,
  },
  milestone: {
    ...typography.labelSm,
    fontFamily: fonts.medium,
    letterSpacing: 0,
    color: colors.onPrimaryMuted,
  },
  milestoneUnlocked: {
    color: colors.tertiary,
  },
  milestoneActive: {
    fontFamily: fonts.bold,
    color: colors.onPrimary,
  },

  // Targets grid
  targets: {
    marginTop: spacing.xs,
    gap: spacing.md,
  },
  targetsTitle: {
    ...typography.headlineSm,
    fontFamily: fonts.bold,
    color: colors.text,
    paddingHorizontal: spacing.xs,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: GRID_GAP,
    // Room for the badges that sit on each tile's top edge.
    rowGap: GRID_GAP + spacing.md,
    paddingTop: spacing.sm,
  },
  tile: {
    borderRadius: 16,
    borderWidth: 2,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    minHeight: 188,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  tileUnlocked: {
    backgroundColor: colors.mintWash,
    borderColor: colors.primaryFixed,
  },
  tileActive: {
    borderColor: colors.primary,
  },
  tileBadge: {
    position: 'absolute',
    top: -11,
    alignSelf: 'center',
    maxWidth: '92%',
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radii.pill,
  },
  tileBadgeUnlocked: {
    backgroundColor: colors.brandMid,
  },
  tileBadgeActive: {
    backgroundColor: colors.primary,
  },
  tileBadgeText: {
    fontFamily: fonts.bold,
    fontSize: 9,
    lineHeight: 12,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: colors.onPrimary,
  },
  tileBadgeTextActive: {
    color: colors.tertiary,
  },
  tileRing: {
    width: TILE_LOGO_RING,
    height: TILE_LOGO_RING,
    borderRadius: TILE_LOGO_RING / 2,
    backgroundColor: colors.surfaceMuted,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileRingUnlocked: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
  },
  faded: {
    opacity: 0.5,
  },
  statusBadge: {
    position: 'absolute',
    width: STATUS_BADGE,
    height: STATUS_BADGE,
    borderRadius: STATUS_BADGE / 2,
    borderWidth: 2,
    borderColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusBadgeCheck: {
    right: -4,
    bottom: -2,
    backgroundColor: colors.secondary,
  },
  statusBadgeLock: {
    right: -4,
    top: -2,
    backgroundColor: colors.textSubtle,
  },
  tileLabel: {
    fontFamily: fonts.bold,
    fontSize: 13,
    lineHeight: 17,
    color: colors.text,
    textAlign: 'center',
  },
  tileLabelLocked: {
    color: colors.textSubtle,
  },
  tileTag: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: spacing.xs,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
  },
  tileTagUnlocked: {
    borderColor: colors.secondary,
    backgroundColor: colors.secondary,
  },
  tileTagActive: {
    borderColor: colors.primaryDeep,
    backgroundColor: colors.primaryDeep,
  },
  tileTagText: {
    fontFamily: fonts.bold,
    fontSize: 11,
    lineHeight: 14,
    color: colors.textMuted,
  },
  tileTagTextOnColor: {
    color: colors.onPrimary,
  },
});

export default Rewards;